import { formatNairaCompact, plural } from '../../services/assistant/format';
import type { PortfolioSnapshot } from '../../services/assistant/types';
import { AssistantAvatar } from './AssistantMessages';

/** Data questions answer instantly; the last ones show the AI's reasoning. */
export const SUGGESTED_PROMPTS = [
  'Give me a summary',
  'Who is overdue?',
  'Which plots are vacant?',
  'Which leases end soon?',
  'What is my collection rate?',
  'How can I get tenants to pay on time?',
];

interface StatProps {
  label: string;
  value: string;
  hint: string;
  tone?: 'default' | 'alert';
}

function Stat({ label, value, hint, tone = 'default' }: StatProps) {
  return (
    <div className="min-w-0 rounded-xl border border-stroke bg-white px-3 py-2 dark:border-strokedark dark:bg-boxdark-2">
      <p className="truncate text-[11px] font-medium uppercase tracking-wide text-bodydark1 dark:text-bodydark2">{label}</p>
      <p className={`text-lg font-bold leading-tight ${tone === 'alert' ? 'text-danger' : 'text-black dark:text-white'}`}>{value}</p>
      <p className="truncate text-[11px] text-bodydark1 dark:text-bodydark2">{hint}</p>
    </div>
  );
}

function StatsRow({ snapshot }: { snapshot: PortfolioSnapshot | null }) {
  if (!snapshot) {
    return (
      <div className="grid grid-cols-3 gap-2" aria-label="Loading your portfolio">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[66px] animate-pulse rounded-xl bg-gray-light dark:bg-boxdark-2" />
        ))}
      </div>
    );
  }
  const { plots, invoices } = snapshot;
  return (
    <div className="grid grid-cols-3 gap-2">
      <Stat
        label="Occupancy"
        value={plots.occupancyRate === null ? '–' : `${plots.occupancyRate}%`}
        hint={`${plots.occupied}/${plural(plots.total, 'plot')}`}
      />
      <Stat
        label="Overdue"
        value={formatNairaCompact(invoices.overdueTotal)}
        hint={plural(invoices.overdue.length, 'invoice')}
        tone={invoices.overdueTotal > 0 ? 'alert' : 'default'}
      />
      <Stat
        label="Collected"
        value={invoices.collectionRate === null ? '–' : `${invoices.collectionRate}%`}
        hint={`of ${formatNairaCompact(invoices.billed)} billed`}
      />
    </div>
  );
}

interface PromptChipsProps {
  prompts: string[];
  onPick: (prompt: string) => void;
  disabled?: boolean;
  layout?: 'wrap' | 'scroll';
}

export function PromptChips({ prompts, onPick, disabled, layout = 'wrap' }: PromptChipsProps) {
  return (
    <div className={layout === 'scroll' ? 'flex gap-2 overflow-x-auto pb-1' : 'flex flex-wrap gap-2'}>
      {prompts.map((p) => (
        <button
          key={p}
          type="button"
          disabled={disabled}
          onClick={() => onPick(p)}
          className="flex-shrink-0 rounded-full border border-stroke bg-white px-3 py-1.5 text-left text-xs font-medium text-body transition-colors hover:border-primary hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-strokedark dark:bg-boxdark-2 dark:text-white/90 dark:hover:border-primary-light dark:hover:text-primary-lighter"
        >
          {p}
        </button>
      ))}
    </div>
  );
}

interface AssistantWelcomeProps {
  firstName?: string;
  snapshot: PortfolioSnapshot | null;
  onPick: (prompt: string) => void;
}

export default function AssistantWelcome({ firstName, snapshot, onPick }: AssistantWelcomeProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2.5">
        <AssistantAvatar />
        <div className="rounded-2xl rounded-tl-md border border-stroke bg-white px-3.5 py-2.5 text-sm leading-relaxed text-body shadow-sm dark:border-strokedark dark:bg-boxdark-2 dark:text-white/90">
          <p className="font-semibold text-black dark:text-white">{firstName ? `Hi ${firstName}!` : 'Hi there!'}</p>
          <p>
            I'm your Re:Loam assistant. Ask me about your plots, tenants, leases and payments. I answer from your own
            records, and can link you straight to the right page.
          </p>
        </div>
      </div>
      <StatsRow snapshot={snapshot} />
      <div>
        <p className="mb-2 text-xs font-medium text-bodydark1 dark:text-bodydark2">Try asking</p>
        <PromptChips prompts={SUGGESTED_PROMPTS} onPick={onPick} />
      </div>
    </div>
  );
}
